import { L } from "../i18n";
// Legal content for R&H Lawyers LLP. Only factual company details from the
// project are used; items requiring confirmation are marked [TO CONFIRM].
import { company } from "../mock";

const contact = `You can contact us at ${company.email} or by post at ${company.address}.`;

const legalDocs__raw = {
  privacy: {
    key: "privacy",
    title: "Privacy Policy",
    updated: "Last updated: September 2026",
    intro:
      "This Privacy Policy explains what personal data we collect through this website, how we use it, and the rights you have under UK data-protection law.",
    sections: [
      {
        h: "Who we are",
        p: `${company.legalName} is a ${company.legalForm} registered in England and Wales (Registration No. ${company.registration}), registered office ${company.address}. The firm is authorised and regulated by the ${company.regulator} (Firm No. ${company.sraFirm}). We act as the data controller for personal data collected via this website.`,
      },
      {
        h: "What data we collect",
        p: "When you use this website you may provide your name, email address, telephone number, the type of client you are, an indicative deposit amount, and any message you send (via the account-registration, illustration, contact and careers forms). If you register for an account, we store your name and email; your password is stored only in hashed form. We also record technical and usage data (e.g. cookie preferences and, where you consent, analytics about how the site is used).",
      },
      {
        h: "How we use your data",
        p: "We use your data to respond to your enquiry, prepare illustrations, review and administer account registrations (including notifying you once your account is approved), operate the client and partner portal, and maintain the security of the platform. Where you consent, we use analytics to improve the website.",
      },
      {
        h: "Lawful basis",
        p: "We process personal data on the basis of: your consent (e.g. analytics cookies), the performance of a contract or steps prior to entering a contract (account registration), our legitimate interests (operating and securing the platform), and our legal and regulatory obligations as an SRA-regulated firm.",
      },
      {
        h: "Who we share it with",
        p: "We do not sell your personal data. Deposits are placed directly with third-party UK-authorised banks and building societies; where you open such accounts, necessary data is shared with those providers. We use trusted hosting and infrastructure providers to run this website. We may disclose data where required by law or our regulator.",
      },
      {
        h: "Retention",
        p: "We keep personal data only for as long as necessary for the purposes described above and to meet legal, regulatory and record-keeping obligations, after which it is securely deleted.",
      },
      {
        h: "Your rights",
        p: "You have the right to access, rectify or erase your personal data, to restrict or object to processing, to data portability, and to withdraw consent at any time (this does not affect processing carried out before withdrawal). " + contact,
      },
      {
        h: "Complaints",
        p: `If you have concerns about how we handle your data, please contact us first. You also have the right to lodge a complaint with the Information Commissioner's Office (ICO), ico.org.uk. Our ICO registration number: [TO CONFIRM]. Data Protection Officer / contact: [TO CONFIRM].`,
      },
    ],
  },

  terms: {
    key: "terms",
    title: "Terms & Conditions",
    updated: "Last updated: September 2026",
    intro:
      "These Terms govern your use of this website. By using it, you accept these Terms. Please read them carefully.",
    sections: [
      {
        h: "About this website",
        p: `This website is operated by ${company.legalName} (Registration No. ${company.registration}), registered office ${company.address}, authorised and regulated by the ${company.regulator} (Firm No. ${company.sraFirm}). It describes a cash-savings platform that provides access to deposit products offered by third-party banks and building societies.`,
      },
      {
        h: "Illustrative figures & no advice",
        p: "Rates, returns and figures shown on this website (including the interest calculator) are illustrative only and are not guaranteed. Nothing on this website constitutes financial, investment, tax or legal advice, and it is not an offer or a recommendation. You should obtain independent professional advice before making any decision.",
      },
      {
        h: "Accounts & registration",
        p: "Registering on this website creates an application that is subject to review and approval by R&H. An account is only opened once we have approved your registration and completed any required checks; registration alone does not create a client account or entitle you to services. We may decline applications at our discretion.",
      },
      {
        h: "Deposits & FSCS",
        p: "Deposits are held with third-party UK-authorised banks and building societies. FSCS protection applies to eligible deposits subject to per-institution limits. We do not ourselves hold client monies except in accordance with the SRA Accounts Rules.",
      },
      {
        h: "Acceptable use",
        p: "You must not misuse this website, attempt to gain unauthorised access, introduce malicious code, or use it in any unlawful way. We may suspend or restrict access where we reasonably consider it necessary.",
      },
      {
        h: "Intellectual property",
        p: "All content and branding on this website is owned by or licensed to us and protected by intellectual-property law. You may view it for personal, non-commercial use but may not reproduce it without permission.",
      },
      {
        h: "Liability",
        p: "We provide this website \u201cas is\u201d and make no warranty that it will be uninterrupted or error-free. To the extent permitted by law, we exclude liability for any loss arising from your use of, or inability to use, this website. Nothing in these Terms excludes liability that cannot be excluded by law.",
      },
      {
        h: "Governing law",
        p: "These Terms are governed by the laws of England and Wales, and the courts of England and Wales have exclusive jurisdiction. " + contact,
      },
    ],
  },

  cookies: {
    key: "cookies",
    title: "Cookie Policy",
    updated: "Last updated: September 2026",
    intro:
      "This Cookie Policy explains what cookies and similar technologies this website uses and how you can manage your preferences.",
    isCookies: true,
    sections: [
      {
        h: "What are cookies?",
        p: "Cookies are small text files stored on your device. We also use similar browser storage (such as localStorage) to remember your choices. Some are essential for the site to work; others are optional.",
      },
      {
        h: "Strictly necessary cookies",
        p: "These are required for the website to function and cannot be switched off. They include your cookie-consent preference and your sign-in session (authentication token) for the client/partner portal. They do not require consent.",
      },
      {
        h: "Analytics cookies (optional)",
        p: "With your consent we use analytics (PostHog) to understand how the website is used so we can improve it. These are non-essential and are only activated after you opt in. You can withdraw consent at any time using the preferences panel below.",
      },
      {
        h: "Marketing cookies (optional)",
        p: "We may use marketing cookies to measure the effectiveness of our communications. These are non-essential and are only set with your consent.",
      },
      {
        h: "Managing your preferences",
        p: "You can accept, reject or customise non-essential cookies at any time using the preferences panel below. Your choice is saved and respected on subsequent visits. Necessary cookies cannot be disabled.",
      },
    ],
  },
};

export const legalDocs = L(legalDocs__raw);
